import * as React from "react";
import {Accordion, AccordionSection, Button, Table, Tooltip} from "@vebox/etui-core";
import {accordionStyle} from "../../ReadAndWrite";
import {groupedMappedCurrentSettings} from "./types";
import {createColumnsForCurrentSettings, mapSettingsToTableData} from "./utils";
import {filterTableData, groupEditableSettingsByGroupName} from "../../ReadAndWrite/utils";
import {useTheme} from "@vebox/etui-system";
import {Theme} from "@vebox/etui-themes";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {PAGES} from "../../pages/MainContentPage";
import {Display} from "../../../components/Display";
import {EditableSetting} from "../../data/models/UpdatingSetting";
import "./styles.scss";
import {ReadAndWriteContext} from "../../../providers/ReadAndWriteProvider";

interface CurrentConfigurationTableProps {
    settings: EditableSetting[],
    isLoading: boolean,
    canEdit: boolean,
    errorMessage: string,
    showEditButton: boolean
}

export const CurrentConfigurationTable: React.FC<CurrentConfigurationTableProps> = ({
                                                                                        settings,
                                                                                        isLoading,
                                                                                        canEdit,
                                                                                        errorMessage,
                                                                                        showEditButton
                                                                                    }) => {
    const {AppTranslations, changePage, getTableHeadersName, registerFilterText} = React.useContext(
        ReadAndWriteContext);
    const theme = useTheme();

    const goTo = (tagName: PAGES) => () => {
        changePage(tagName)
    }

    const renderBack = () => {
        return (
            <Display isVisible={showEditButton}>
                <Tooltip
                    disableToolTip={canEdit}
                    message={errorMessage}
                    placement={"left"}
                    styles={{bubble: {width: "200px"}}}
                >
                    <Button
                        styles={{
                            root: {bg: "#969696", width: "24px", height: "24px"},
                            text: {color: "white"}
                        }}
                        iconOnly={true}
                        onClick={goTo(PAGES.EDITING)}
                        disabled={!canEdit}
                        iconProps={{symbol: 'edit', weight: '400'}}
                        variant={"secondary"}
                        shape={"circle"}
                    >Edit</Button>
                </Tooltip>
            </Display>
        );
    }

    const groupedSettings = React.useMemo(() => {
        return groupEditableSettingsByGroupName(settings?.filter(filterTableData(registerFilterText)))
    }, [settings, registerFilterText]);

    const dataSource: groupedMappedCurrentSettings = React.useMemo(() => {
        const result: groupedMappedCurrentSettings = {};
        groupedSettings.forEach((group) => {
            result[group.groupName] = mapSettingsToTableData(group.registers);
        });
        return result;
    }, [groupedSettings]);

    return (
        <VeBoxCard className={"Section rw-card"}>
            <VeBoxCard.Header
                className={"rw-card-header"}
                title={AppTranslations.readAndWrite.parameters}
                back={renderBack()}
                singleLineTitle={true}
            />
            <VeBoxCard.Body className={"rw-card-body no-padding"}>
                <Accordion>
                    {groupedSettings.map((accordionSection, index) => (
                        <AccordionSection
                            key={index}
                            defaultOpen={index === 0}
                            name={accordionSection.groupName}
                            keepSectionContentInDOM={false}
                            styles={{
                                ...accordionStyle(theme as Theme),
                                sectionContent: {height: ((accordionSection.registers.length + 1) * 39.6) + "px"}
                            }}
                        >
                            <Table
                                pagination={false}
                                loading={isLoading}
                                dataSource={dataSource[accordionSection.groupName]}
                                columns={createColumnsForCurrentSettings(getTableHeadersName)}
                            />
                        </AccordionSection>
                    ))}
                </Accordion>
            </VeBoxCard.Body>
        </VeBoxCard>
    );
};
